import { Component, Inject, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PingUser, PingUserUpdateRequest } from '../../models/ping-user.model';
import { PiamService } from '../../services/piam.service';

export interface PingOneModalData {
  pingUsers: PingUser[];
  patientName: string;
  patientMpi: string;
}

@Component({
  selector: 'app-pingone-modal',
  templateUrl: './pingone-modal.component.html',
  styleUrls: ['./pingone-modal.component.scss'],
  standalone: false
})
export class PingOneModalComponent {
  private fb = inject(FormBuilder);
  private piamService = inject(PiamService);

  // Track which account is expanded (by index)
  expandedIndex: number | null = 0;

  // Track which account is in edit mode (by index)
  editingIndex: number | null = null;

  isSaving = false;
  saveSuccess = false;
  saveError: string | null = null;

  // Edit forms for each account (created on demand)
  editForms: Map<number, FormGroup> = new Map();

  constructor(
    public dialogRef: MatDialogRef<PingOneModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PingOneModalData
  ) {
    // Start with all accounts collapsed
    this.expandedIndex = null;
  }

  get pingUsers(): PingUser[] {
    return this.data.pingUsers || [];
  }

  get hasMultipleAccounts(): boolean {
    return this.pingUsers.length > 1;
  }

  toggleAccount(index: number): void {
    // If clicking on the expanded account and not in edit mode, collapse it
    if (this.expandedIndex === index && this.editingIndex !== index) {
      this.expandedIndex = null;
    } else {
      // Cancel any active edit
      if (this.editingIndex !== null) {
        this.cancelEdit();
      }
      this.expandedIndex = index;
    }
  }

  isExpanded(index: number): boolean {
    return this.expandedIndex === index;
  }

  isEditing(index: number): boolean {
    return this.editingIndex === index;
  }

  startEdit(index: number): void {
    const user = this.pingUsers[index];
    if (!user) return;

    // Create form for this account if not exists
    if (!this.editForms.has(index)) {
      this.editForms.set(index, this.fb.group({
        mpi: [user.mpi || '', Validators.required],
        email: [user.email || '', [Validators.email]],
        mobilePhone: [user.mobilePhone || ''],
        primaryPhone: [user.primaryPhone || '']
      }));
    } else {
      // Reset form values
      this.editForms.get(index)?.patchValue({
        mpi: user.mpi || '',
        email: user.email || '',
        mobilePhone: user.mobilePhone || '',
        primaryPhone: user.primaryPhone || ''
      });
    }

    this.editingIndex = index;
    this.saveError = null;
    this.saveSuccess = false;
  }

  cancelEdit(): void {
    this.editingIndex = null;
    this.saveError = null;
  }

  getEditForm(index: number): FormGroup | null {
    return this.editForms.get(index) || null;
  }

  saveChanges(index: number): void {
    const form = this.editForms.get(index);
    const user = this.pingUsers[index];

    if (!form || form.invalid || !user) {
      return;
    }

    // Require externalId to update
    if (!user.externalId) {
      this.saveError = 'Cannot update: No PingOne external ID';
      return;
    }

    this.isSaving = true;
    this.saveError = null;
    this.saveSuccess = false;

    // Build update request with only changed fields
    const request: PingUserUpdateRequest = {
      externalId: user.externalId
    };

    // Only include fields that have values
    const mpi = form.get('mpi')?.value;
    const email = form.get('email')?.value;
    const mobilePhone = form.get('mobilePhone')?.value;
    const primaryPhone = form.get('primaryPhone')?.value;

    if (mpi) request.mpi = mpi;
    if (email) request.email = email;
    if (mobilePhone) request.mobilePhone = mobilePhone;
    if (primaryPhone) request.primaryPhone = primaryPhone;

    // Call PIAM service to update user
    this.piamService.updateUser(request).subscribe({
      next: (response) => {
        this.isSaving = false;

        if (response.success && response.user) {
          this.saveSuccess = true;
          this.editingIndex = null;

          // Update local data with response from server
          const updatedUser = {
            ...user,
            ...response.user,
            status: response.user.status || user.status
          };
          this.data.pingUsers[index] = updatedUser;

          // Clear success message after delay
          setTimeout(() => {
            this.saveSuccess = false;
          }, 3000);
        } else {
          this.saveError = response.error || 'Update failed';
        }
      },
      error: (err) => {
        this.isSaving = false;
        this.saveError = err.message || 'Network error occurred';
        console.error('PIAM update error:', err);
      }
    });
  }

  getStatusClass(user: PingUser): string {
    return user.status || 'unknown';
  }

  getStatusLabel(user: PingUser): string {
    switch (user.status) {
      case 'active': return 'Active';
      case 'inactive': return 'Inactive';
      case 'locked': return 'Locked';
      default: return 'Unknown';
    }
  }

  close(): void {
    this.dialogRef.close({ updatedUsers: this.data.pingUsers });
  }

  formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '—';
    try {
      // Handle MMDDYYYY format (e.g., "05261994")
      if (/^\d{8}$/.test(dateStr)) {
        const month = parseInt(dateStr.substring(0, 2), 10) - 1;
        const day = parseInt(dateStr.substring(2, 4), 10);
        const year = parseInt(dateStr.substring(4, 8), 10);
        const parsedDate = new Date(year, month, day);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
        }
      }

      // Handle various date formats (YYYY-MM-DD, MM/DD/YYYY, etc.)
      const date = new Date(dateStr);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        // Try parsing with delimiters
        const parts = dateStr.split(/[-\/]/);
        if (parts.length === 3) {
          let year: number, month: number, day: number;
          if (parts[0].length === 4) {
            // YYYY-MM-DD format
            year = parseInt(parts[0], 10);
            month = parseInt(parts[1], 10) - 1;
            day = parseInt(parts[2], 10);
          } else {
            // MM-DD-YYYY or MM/DD/YYYY format
            month = parseInt(parts[0], 10) - 1;
            day = parseInt(parts[1], 10);
            year = parseInt(parts[2], 10);
          }
          const parsedDate = new Date(year, month, day);
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            });
          }
        }
        return dateStr; // Return original if parsing fails
      }
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  formatDateTime(dateStr: string | undefined): string {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  }
}
